import React from "react";
import { Link } from "react-router-dom";

/**
 * ExtensionTerms · iter v28 r350
 *
 * Terms of Use SPECIFICI per l'estensione Chrome "Pronto Publisher".
 * Pagina pubblica (no auth, indicizzabile) accessibile da:
 *   - link nel footer del pannello flottante (helper.js)
 *   - manifest.json dell'estensione (per audit Chrome Web Store)
 *   - chiunque cerchi "pronto publisher termini" su Google
 *
 * Speculare a /app/extension/terms.html (la versione interna all'estensione).
 * Quando aggiorni i contenuti, aggiorna ENTRAMBI i file E bumpa TOS_VERSION
 * in popup.js per richiedere nuovo consenso utente.
 */
export default function ExtensionTerms() {
  const lastUpdated = "25 febbraio 2026";

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1C1C1A]" data-testid="extension-terms-page">
      <header className="border-b border-[#EAE7DF] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-editorial text-2xl tracking-tight text-[#1C1C1A]" data-testid="ext-terms-home-link">
            Pronto
          </Link>
          <Link
            to="/auth"
            className="text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] transition-colors"
            data-testid="ext-terms-login-link"
          >
            Accedi →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-3">
          Estensione Chrome · Pronto Publisher
        </div>
        <h1 className="font-editorial text-4xl sm:text-5xl tracking-tight mb-3" data-testid="ext-terms-title">
          Termini d'uso
        </h1>
        <p className="text-[13px] text-[#9A968B] italic mb-10">
          Ultimo aggiornamento: {lastUpdated}
        </p>

        <div className="bg-[#FAF8F3] border border-[#E5E2D8] rounded-2xl px-6 py-5 mb-10">
          <p className="text-[14px] leading-[1.65] text-[#1C1C1A] m-0">
            <strong>In breve:</strong> Pronto Publisher è un assistente alla compilazione di form sui portali immobiliari. Compila per te i campi che hai già nel tuo annuncio Pronto, ma <strong>non pubblica nulla in automatico</strong>: il bottone "Pubblica" resta sempre nelle tue mani.
          </p>
        </div>

        <Section title="1 · Cosa fa l'estensione">
          <p>
            Pronto Publisher legge dal tuo account Pronto (al quale accedi con le tue credenziali) le informazioni di un annuncio e le inserisce nei campi corrispondenti del wizard di pubblicazione di Immobiliare.it e Idealista, <strong>mentre sei già loggato sul tuo account personale</strong> del portale.
          </p>
          <p><strong>Su Idealista</strong> compila in automatico la maggior parte dei campi (~95%): titolo, prezzo, m², locali, indirizzo, classe energetica, contatti.</p>
          <p><strong>Su Immobiliare.it</strong> il portale blocca la scrittura programmatica della maggior parte dei campi. L'estensione compila in automatico solo <strong>Prezzo</strong>, <strong>Descrizione</strong> e <strong>Contatti</strong>; per tutti gli altri campi (Categoria, Tipologia, Comune, Via, Superficie, Locali, Piano, Classe energetica, Stato, Esposizione, Infissi…) mostra il valore già pronto con il bottone <strong>Copia</strong>, così fai copia-incolla senza passare da Pronto ogni volta.</p>
          <p><strong>In entrambi i portali</strong> foto e planimetria le carichi tu manualmente dal Desktop (gli uploader sono proprietari e non accettano file da fuori).</p>
        </Section>

        <Section title="2 · Cosa l'estensione NON fa">
          <ul>
            <li><strong>Non clicca mai il bottone "Pubblica" / "Conferma annuncio"</strong>. La pubblicazione è sempre e solo un click manuale tuo.</li>
            <li><strong>Non aggira CAPTCHA</strong>, controlli "non sono un robot", verifiche SMS o altri sistemi anti-bot. Se il portale chiede una verifica, la fai tu a mano.</li>
            <li><strong>Non estrae dati altrui</strong> dai portali (no scraping di annunci di colleghi o concorrenti).</li>
            <li><strong>Non carica le foto</strong> al posto tuo: il drag &amp; drop delle immagini sul portale lo fai sempre tu.</li>
            <li><strong>Non accede ai tuoi account</strong> dei portali con username/password: tu sei loggato sul portale prima che l'estensione faccia qualunque cosa.</li>
          </ul>
        </Section>

        <Section title="3 · Termini dei portali e tua responsabilità">
          <div className="bg-[#FFF4E6] border border-[#F4C57B] rounded-xl px-5 py-4 mb-4">
            <p className="m-0">
              I Termini di Servizio di Immobiliare.it e Idealista vietano in linea generale l'<em>accesso automatizzato</em>. Pronto Publisher è progettato per restare un <strong>assistente alla compilazione</strong> simile a un password manager o a un autofill del browser, ma <strong>resta tua responsabilità</strong> rispettare i termini specifici del portale dove pubblichi.
            </p>
          </div>
          <p>Per ridurre al minimo qualunque rischio l'estensione:</p>
          <ul>
            <li>Funziona solo dopo che ti sei loggato manualmente sul portale;</li>
            <li>Aspetta minimo 4 secondi tra un campo e l'altro (no comportamento "robot");</li>
            <li>Lascia sempre il click finale di pubblicazione a te;</li>
            <li>Mostra un avviso quando un campo è bloccato dall'anti-bot del portale e ti chiede di compilarlo a mano.</li>
          </ul>
          <p>
            Se il portale dovesse sospendere o limitare il tuo account perché ti ritiene "automatizzato", la responsabilità ricade sul tuo uso dell'estensione: ti consigliamo di interromperne l'uso su quel portale e di contattarci.
          </p>
        </Section>

        <Section title="4 · Cosa NON devi mai fare">
          <div className="bg-[#FDEDED] border border-[#E8A0A0] rounded-xl px-5 py-4">
            <ul className="m-0">
              <li>Usare strumenti che <strong>aggirano i CAPTCHA</strong> insieme a Pronto Publisher (in Italia l'accesso abusivo a sistemi protetti è reato — art. 615-ter e 615-quater c.p.);</li>
              <li>Modificare il codice dell'estensione per <strong>cliccare automaticamente "Pubblica"</strong>;</li>
              <li>Usare l'estensione su account che non sono i tuoi o senza un mandato a vendere firmato dal proprietario dell'immobile;</li>
              <li>Estrarre annunci altrui dai portali per importarli in Pronto.</li>
            </ul>
          </div>
        </Section>

        <Section title="5 · Dati personali e privacy">
          <p>L'estensione tratta esclusivamente:</p>
          <ul>
            <li>Le credenziali di accesso al tuo account Pronto (salvate solo nel tuo browser, in <code>chrome.storage.local</code>, mai inviate ad altri server);</li>
            <li>I dati dell'annuncio che <strong>tu hai inserito</strong> in Pronto e che hai deciso di pubblicare;</li>
            <li>Un identificativo della sessione di pubblicazione (token) usato esclusivamente per ricaricare i dati nella scheda del portale aperta.</li>
          </ul>
          <p>
            Pronto Publisher <strong>non legge</strong> il contenuto di pagine diverse da Immobiliare.it, Idealista e prontoimmo.it. Nessun tracking esterno, nessuna analytics, nessuna telemetria.
          </p>
          <p>
            Il trattamento dei dati dei venditori che inserisci negli annunci ricade sotto il tuo accordo (mandato a vendere) e sotto la <Link to="/privacy-extension" className="underline text-[#5B6B4E]">Privacy Policy dell'estensione</Link>.
          </p>
        </Section>

        <Section title="6 · Limitazioni di responsabilità">
          <p>
            Pronto Publisher viene fornita "così com'è" come strumento di produttività. Pronto / Emergent Labs non garantisce che il portale immobiliare accetterà sempre la compilazione automatica né che la tua pubblicazione abbia successo, e <strong>non è responsabile</strong> di sospensioni, ban, multe contrattuali o danni reputazionali derivanti dall'utilizzo dell'estensione in violazione dei termini di servizio dei singoli portali.
          </p>
          <p>
            Se hai dubbi specifici sulla compatibilità con i termini di un portale o su un caso d'uso particolare, ti consigliamo di consultare un legale specializzato in diritto digitale e e-commerce per la tua giurisdizione.
          </p>
        </Section>

        <Section title="7 · Contatti e revoca del consenso">
          <p>
            Per domande, segnalazioni, richiesta di cancellazione dei dati o disattivazione dell'estensione su un portale specifico: <a href="mailto:supporto@prontoimmo.it" className="underline text-[#5B6B4E]">supporto@prontoimmo.it</a>.
          </p>
          <p>
            Puoi rimuovere l'estensione in qualunque momento da <code>chrome://extensions/</code>. Tutti i dati salvati localmente nel browser verranno cancellati con la disinstallazione.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-[#E8E5DE] text-[12px] text-[#9A968B]">
          © 2026 Pronto · Emergent Labs · Tutti i diritti riservati.<br />
          Pronto Publisher è un prodotto indipendente, non affiliato a Immobiliare.it o Idealista.
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8" data-testid={`section-${title.split(" ")[0]}`}>
      <h2 className="text-[18px] font-semibold mb-3 text-[#1C1C1A]">{title}</h2>
      <div className="text-[14px] leading-[1.7] text-[#3F3D38] space-y-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_code]:bg-[#EAE7DF] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12.5px]">
        {children}
      </div>
    </section>
  );
}
